// src/components/MaterialsGateCard.tsx
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Package, CheckCircle2, AlertTriangle, ExternalLink, Loader2, ArrowLeftRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { StatusBadge } from "@/components/StatusBadge";
import { useRole } from "@/data/RoleContext";
import { VendorPOService } from "@/services/vendor.service";
import { WarehouseService } from "@/services/warehouse.service";
import type { ShipmentStatus } from "@/data/WarehouseInventoryContext";
import type { TrackingStatusTag, POTrackingEntry, PurchaseOrder } from "@/data/VendorPOContext";
import type { JobAllocation } from "@/data/WarehouseInventoryContext";
import type { Issue, MaterialLineItem, Job } from "@/data/types";

// ─── Derivation types ────────────────────────────────────────────────────────

export type PoGateState = {
  poId: string;
  poNumber: string;
  vendorName: string;
  estimatedEta?: string;
  warehouseName?: string | null;
  status: PurchaseOrder["status"];
  pendingAction: "confirm_in_transit" | "confirm_delivered" | "confirm_received" | null;
  trackingEntries: POTrackingEntry[];
};

export type AllocationGateState = {
  total: number;
  readyCount: number;
  pendingIds: string[];
  isDone: boolean;
} | null;

export type IssuesGateState = {
  openCount: number;
  types: string[];
} | null;

export type StockGateState = {
  outOfStockItems: string[];
  isDone: boolean;
} | null;

export type ReallocationGateState = {
  count: number;
  items: string[];
} | null;

// ─── Derivation functions (exported for unit tests) ──────────────────────────

export function derivePoGates(purchaseOrders: PurchaseOrder[]): PoGateState[] {
  return purchaseOrders
    .filter((po) => po.status !== "Draft")
    .map((po) => {
      // Only gate action on the job detail side is receiving the shipment.
      // Ordering → In Transit → Delivered confirmations are handled on the Ordering page.
      const pendingAction: PoGateState["pendingAction"] =
        po.status === "Delivered" ? "confirm_received" : null;

      return {
        poId: po.poId,
        poNumber: po.poNumber,
        vendorName: po.vendorName,
        estimatedEta: po.estimatedEta,
        warehouseName: po.warehouseName,
        status: po.status,
        pendingAction,
        trackingEntries: (po.trackingNumbers ?? []).filter((e) => Boolean(e.trackingNumber && e.trackingStatus)),
      };
    });
}

export function deriveAllocationGate(jobAllocations: JobAllocation[]): AllocationGateState {
  if (jobAllocations.length === 0) return null;
  const pending = jobAllocations.filter(
    (a) => a.status !== "Ready for Install" && a.status !== "Installed"
  );
  return {
    total: jobAllocations.length,
    readyCount: jobAllocations.length - pending.length,
    pendingIds: pending.map((a) => a.id),
    isDone: pending.length === 0,
  };
}

export function deriveIssuesGate(jobIssues: Issue[]): IssuesGateState {
  const open = jobIssues.filter((i) => i.status === "Open" || i.status === "In Progress");
  if (open.length === 0) return null;
  const types = [...new Set(open.map((i) => i.issueType))];
  return { openCount: open.length, types };
}

export function deriveStockGate(jobMaterials: MaterialLineItem[]): StockGateState {
  if (jobMaterials.length === 0) return null;
  const lowStock = jobMaterials.filter((m) => m.inventoryStatus === "Low Stock");
  return {
    outOfStockItems: lowStock.map((m) => m.itemName).filter(Boolean),
    isDone: lowStock.length === 0,
  };
}

export function deriveReallocationGate(jobAllocations: JobAllocation[]): ReallocationGateState {
  const reAllocated = jobAllocations.filter(
    (a) => typeof a.allocatedBy === "string" && a.allocatedBy.toLowerCase().includes("reallocation"),
  );
  if (reAllocated.length === 0) return null;
  return {
    count: reAllocated.length,
    items: [...new Set(reAllocated.map((a) => a.productName).filter(Boolean))],
  };
}

// ─── Constants ───────────────────────────────────────────────────────────────

const ACTION_ROLES = new Set(["warehouse_manager", "ops_manager", "admin", "super_admin"]);

const ACTION_LABELS: Record<NonNullable<PoGateState["pendingAction"]>, string> = {
  confirm_in_transit: "Confirm In Transit?",
  confirm_delivered: "Confirm Delivered?",
  confirm_received: "Confirm Received?",
};

const ACTION_TARGET_STATUS: Record<NonNullable<PoGateState["pendingAction"]>, PurchaseOrder["status"]> = {
  confirm_in_transit: "In Transit",
  confirm_delivered: "Delivered",
  confirm_received: "Received",
};

// Stage order used to determine completed ✓ rows per PO
const PO_STAGE_ORDER: PurchaseOrder["status"][] = [
  "Submitted",
  "Confirmed",
  "In Transit",
  "Delivered",
  "Received",
];

function trackingTone(tag: TrackingStatusTag | undefined): string {
  if (tag === "Delivered") return "text-emerald-600 dark:text-emerald-400";
  if (tag === "InTransit" || tag === "OutForDelivery") return "text-amber-600 dark:text-amber-400";
  if (tag === "Exception" || tag === "Expired") return "text-red-600 dark:text-red-400";
  return "text-muted-foreground";
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function DoneRow({ label, detail }: { label: string; detail?: string }) {
  return (
    <div className="flex items-center gap-1.5 py-1 text-[11px] text-muted-foreground">
      <CheckCircle2 className="h-3 w-3 shrink-0 text-good" />
      <span className="font-medium">{label}</span>
      {detail && <span className="text-muted-foreground/70">· {detail}</span>}
    </div>
  );
}

function PoGateSection({
  gate,
  canAct,
  dismissed,
  onDismiss,
  onConfirm,
}: {
  gate: PoGateState;
  canAct: boolean;
  dismissed: Set<string>;
  onDismiss: (key: string) => void;
  onConfirm: (poId: string, action: NonNullable<PoGateState["pendingAction"]>) => Promise<void>;
}) {
  const [loading, setLoading] = useState(false);

  const currentIdx = PO_STAGE_ORDER.indexOf(gate.status);
  const warehouseLabel = gate.warehouseName ? ` · ${gate.warehouseName}` : "";
  const completedStages: Array<{ label: string; detail?: string }> = [];
  if (currentIdx >= 0) completedStages.push({ label: "Ordered", detail: gate.poNumber });
  if (currentIdx >= 2) completedStages.push({ label: "In Transit", detail: `${gate.vendorName}${warehouseLabel}` });
  if (currentIdx >= 3) completedStages.push({ label: "Delivered", detail: `${gate.vendorName}${warehouseLabel}` });
  if (currentIdx >= 4) completedStages.push({ label: "Received", detail: `${gate.poNumber}${warehouseLabel}` });

  const dismissKey = gate.pendingAction ? `${gate.poId}:${gate.pendingAction}` : null;
  const isDismissed = dismissKey ? dismissed.has(dismissKey) : false;

  const handleYes = async () => {
    if (!gate.pendingAction) return;
    setLoading(true);
    try {
      await onConfirm(gate.poId, gate.pendingAction);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-0.5">
      {completedStages.map((s) => (
        <DoneRow key={s.label} label={s.label} detail={s.detail} />
      ))}

      {/* Compact tracking status per registered entry */}
      {gate.trackingEntries.length > 0 && (
        <div className="pl-4 space-y-0.5">
          {gate.trackingEntries.map((entry) => (
            <div key={entry.id} className="flex items-center gap-1.5">
              <span className="text-[10px] font-mono text-muted-foreground/70 truncate max-w-[90px]">
                {entry.trackingNumber}
              </span>
              {entry.trackingStatus && (
                <span className={`text-[10px] font-medium shrink-0 ${trackingTone(entry.trackingStatus)}`}>
                  {entry.trackingStatus}
                </span>
              )}
              {entry.lastEventDescription && (
                <span className="text-[10px] text-muted-foreground truncate">
                  · {entry.lastEventDescription}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {gate.pendingAction && !isDismissed && (
        <div className="mt-1.5 rounded border border-border/50 bg-muted/20 p-2 space-y-1.5">
          <p className="text-[11px] font-semibold text-foreground">
            {ACTION_LABELS[gate.pendingAction]}
          </p>
          <p className="text-[10px] text-muted-foreground">
            {gate.poNumber} · {gate.vendorName}
            {gate.warehouseName && ` · ${gate.warehouseName}`}
            {gate.estimatedEta && ` · ETA: ${gate.estimatedEta}`}
          </p>
          {canAct && (
            <div className="flex gap-1.5">
              <Button
                size="sm"
                className="h-6 px-3 text-[11px]"
                disabled={loading}
                onClick={handleYes}
              >
                {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : "Yes"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-6 px-3 text-[11px]"
                disabled={loading}
                onClick={() => dismissKey && onDismiss(dismissKey)}
              >
                No
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AllocationGateSection({
  gate,
  canAct,
  onMarkReady,
}: {
  gate: NonNullable<AllocationGateState>;
  canAct: boolean;
  onMarkReady: () => Promise<void>;
}) {
  const [loading, setLoading] = useState(false);

  const handleMarkReady = async () => {
    setLoading(true);
    try {
      await onMarkReady();
    } finally {
      setLoading(false);
    }
  };

  if (gate.isDone) {
    return (
      <DoneRow
        label="Allocation"
        detail={`${gate.total} item${gate.total !== 1 ? "s" : ""} ready`}
      />
    );
  }

  return (
    <div className="mt-1.5 rounded border border-border/50 bg-muted/20 p-2 space-y-1.5">
      <p className="text-[11px] font-semibold text-foreground">Allocation</p>
      <p className="text-[10px] text-muted-foreground">
        {gate.readyCount} of {gate.total} item{gate.total !== 1 ? "s" : ""} Ready for Install
      </p>
      {canAct && (
        <Button
          size="sm"
          variant="outline"
          className="h-6 px-3 text-[11px]"
          disabled={loading}
          onClick={handleMarkReady}
        >
          {loading && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
          Mark Remaining Ready
        </Button>
      )}
    </div>
  );
}

function IssuesGateSection({ gate }: { gate: NonNullable<IssuesGateState> }) {
  return (
    <div className="flex items-start gap-1.5 rounded border border-warn/30 bg-warn/5 px-2 py-1.5 mt-1">
      <AlertTriangle className="h-3 w-3 shrink-0 text-warn mt-0.5" />
      <div className="space-y-0.5">
        <p className="text-[11px] font-semibold text-warn">
          {gate.openCount} open issue{gate.openCount !== 1 ? "s" : ""}
        </p>
        <p className="text-[10px] text-muted-foreground">{gate.types.join(", ")}</p>
      </div>
    </div>
  );
}

function StockGateSection({ gate }: { gate: NonNullable<StockGateState> }) {
  if (gate.isDone) {
    return <DoneRow label="Stock" detail="All items in stock" />;
  }
  return (
    <div className="flex items-start gap-1.5 rounded border border-warn/30 bg-warn/5 px-2 py-1.5 mt-1">
      <AlertTriangle className="h-3 w-3 shrink-0 text-warn mt-0.5" />
      <div className="space-y-0.5">
        <p className="text-[11px] font-semibold text-warn">Low Stock</p>
        <p className="text-[10px] text-muted-foreground">
          {gate.outOfStockItems.slice(0, 3).join(", ")}
          {gate.outOfStockItems.length > 3 && ` +${gate.outOfStockItems.length - 3} more`}
        </p>
      </div>
    </div>
  );
}

function ReallocationGateSection({ gate }: { gate: NonNullable<ReallocationGateState> }) {
  return (
    <div className="flex items-start gap-1.5 rounded border border-border/50 bg-muted/20 px-2 py-1.5 mt-1">
      <ArrowLeftRight className="h-3 w-3 shrink-0 text-muted-foreground mt-0.5" />
      <div className="space-y-0.5">
        <p className="text-[11px] font-semibold text-foreground">
          {gate.count} item{gate.count !== 1 ? "s" : ""} re-allocated to this job
        </p>
        {gate.items.length > 0 && (
          <p className="text-[10px] text-muted-foreground">
            {gate.items.slice(0, 3).join(", ")}
            {gate.items.length > 3 && ` +${gate.items.length - 3} more`}
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Props ───────────────────────────────────────────────────────────────────

interface MaterialsGateCardProps {
  job: Job;
  purchaseOrders: PurchaseOrder[];
  jobAllocations: JobAllocation[];
  jobIssues: Issue[];
  jobMaterials: MaterialLineItem[];
  orderingBypassed: boolean;
  onRefetch: () => Promise<void>;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function MaterialsGateCard({
  job,
  purchaseOrders,
  jobAllocations,
  jobIssues,
  jobMaterials,
  orderingBypassed,
  onRefetch,
}: MaterialsGateCardProps) {
  const { role } = useRole();
  const canAct = ACTION_ROLES.has(role);

  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [actingPoId, setActingPoId] = useState<string | null>(null);
  const [markingReady, setMarkingReady] = useState(false);

  const poGates = derivePoGates(purchaseOrders);
  const allocationGate = deriveAllocationGate(jobAllocations);
  const issuesGate = deriveIssuesGate(jobIssues);
  const stockGate = deriveStockGate(jobMaterials);
  const reallocationGate = deriveReallocationGate(jobAllocations);

  const handleDismiss = (key: string) => {
    setDismissed((prev) => new Set([...prev, key]));
  };

  const handlePoConfirm = async (
    poId: string,
    action: NonNullable<PoGateState["pendingAction"]>
  ) => {
    setActingPoId(poId);
    const targetStatus = ACTION_TARGET_STATUS[action];
    try {
      await VendorPOService.updatePurchaseOrder(poId, { status: targetStatus });
      // Keep shipments table in sync so Warehouse → Incoming Shipments reflects the change.
      await WarehouseService.syncShipmentsStatusForPo(poId, targetStatus as ShipmentStatus);
      await onRefetch();
      toast.success(`Materials marked as ${targetStatus}`);
    } catch {
      toast.error("Failed to update PO status");
    } finally {
      setActingPoId(null);
    }
  };

  const handleMarkAllReady = async () => {
    if (!allocationGate || allocationGate.pendingIds.length === 0) return;
    setMarkingReady(true);
    try {
      await Promise.all(
        allocationGate.pendingIds.map((id) =>
          WarehouseService.updateJobAllocation(id, { status: "Ready for Install" })
        )
      );
      await onRefetch();
      toast.success("All materials marked Ready for Install");
    } catch {
      toast.error("Failed to update allocations");
    } finally {
      setMarkingReady(false);
    }
  };

  return (
    <Card className="shadow-none">
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-center justify-between pb-1.5 mb-2 border-b">
          <div className="flex items-center gap-2">
            <Package className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em]">
              Materials
            </h3>
          </div>
          <Link
            to="/warehouse"
            className="text-[10px] text-primary hover:underline inline-flex items-center gap-0.5"
          >
            Warehouse <ExternalLink className="h-2.5 w-2.5" />
          </Link>
        </div>

        {/* Summary rows — Planning / Readiness / Missing */}
        <div className="divide-y mb-2">
          <div className="flex items-center justify-between py-1.5">
            <span className="text-xs text-muted-foreground">Planning</span>
            <span className="text-xs font-medium text-right">
              {orderingBypassed ? "Ordering Bypassed" : (job.planningStatus || "Not Started")}
            </span>
          </div>
          <div className="flex items-center justify-between py-1.5">
            <span className="text-xs text-muted-foreground">Readiness</span>
            <span className="text-xs font-medium text-right">
              {orderingBypassed
                ? "Material gate bypassed"
                : <StatusBadge type="materialReadiness" value={job.materialReadiness} />}
            </span>
          </div>
          {!orderingBypassed && job.missingItemsList && (
            <div className="flex items-center justify-between py-1.5">
              <span className="text-xs text-muted-foreground">Missing</span>
              <span className="text-xs font-medium text-right text-destructive">{job.missingItemsList}</span>
            </div>
          )}
        </div>

        {/* Body */}
        {orderingBypassed ? (
          <div className="text-[11px] text-muted-foreground">Ordering Bypassed</div>
        ) : (
          <div className="space-y-0.5">
            {poGates.length === 0 ? (
              <div className="text-[11px] text-muted-foreground py-1">No purchase orders yet.</div>
            ) : (
              poGates.map((gate) => (
                <PoGateSection
                  key={gate.poId}
                  gate={gate}
                  canAct={canAct && actingPoId === null}
                  dismissed={dismissed}
                  onDismiss={handleDismiss}
                  onConfirm={handlePoConfirm}
                />
              ))
            )}

            {allocationGate && (
              <AllocationGateSection
                gate={allocationGate}
                canAct={canAct && !markingReady}
                onMarkReady={handleMarkAllReady}
              />
            )}

            {issuesGate && <IssuesGateSection gate={issuesGate} />}
            {stockGate && <StockGateSection gate={stockGate} />}
            {reallocationGate && <ReallocationGateSection gate={reallocationGate} />}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
